"use client"

import { useRef, useState, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { HeadlineHero, LawnPrefillBanner, RelatedTools, ResultEmailCaptureCard, ResultSuccessCard, ToolPageLoadingFallback } from "@/components/tools/shared"
import { buildRelatedToolUrl, buildToolUrl } from "@/lib/result-tool-urls"
import { toolNavigationAnalytics, analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { MowingForm } from "@/components/tools/mowing-form"
import { MowingResults } from "@/components/tools/mowing-results"
import { type MowingResult } from "@/lib/mowing-calculator"
import { useDefaultLawn, getCurrentSeason } from "@/hooks/use-default-lawn"
import { useAuth } from "@/contexts/AuthContext"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { motion } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"
import { useFromTool } from "@/hooks/use-from-tool"
import { MowingStripeStudioCta } from "@/components/tools/mowing-stripe-studio-cta"
import type { MowerTypeId } from "@/lib/mowing-plan"

function MowingCalculatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("mowing-calculator")
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const { subscription } = useAuth()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } =
    useNewsletterPrompt()
  const [result, setResult] = useState<MowingResult | null>(null)
  const [mowerType, setMowerType] = useState<MowerTypeId>("rotary")
  const trackedUsageRef = useRef(false)

  const handleResultChange = (calculationResult: MowingResult | null) => {
    setResult(calculationResult)
    if (calculationResult && !trackedUsageRef.current) {
      trackedUsageRef.current = true
      trackUsage("mowing-calculator")
      analytics.calculatorUsed("mowing", {
        inputs: {
          grass_type: calculationResult.grassType,
          season: calculationResult.currentSeason,
        },
        result: {
          grass_season: calculationResult.grassSeason,
          height_optimal: calculationResult.mowingHeight.optimal,
          height_min: calculationResult.mowingHeight.minimum,
          height_max: calculationResult.mowingHeight.maximum,
          frequency_growing: calculationResult.mowingFrequency.growingSeason,
          frequency_peak: calculationResult.mowingFrequency.peakGrowth,
          current_season: calculationResult.currentSeason,
          current_height: calculationResult.currentRecommendations.height,
          current_frequency: calculationResult.currentRecommendations.frequency,
        },
        userTier: subscription?.isPremium ? "premium" : "free",
        source: "live_update",
      })
    }
  }

  // suppress unused-import warning for getCurrentSeason (used by form internally)
  void getCurrentSeason

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Mowing Height Calculator" },
          ]}
        />

        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="mb-8 md:mb-12">
            <HeadlineHero
              eyebrow="Tuned to your grass"
              title="How high should you"
              accent="cut?"
              subtitle="Free mowing height calculator by grass type and season. The blade height and mow-when threshold update on a live ruler as you choose."
            />
          </div>

          {/* Form */}
          <div className="mb-8">
            <LawnPrefillBanner
              lawns={lawns}
              selectedLawnId={selectedLawnId}
              onSelect={setSelectedLawnId}
            />
            <MowingForm
              initialValues={{
                zipCode: searchParams.get("zip") ?? lawnPrefill.zipCode,
                grassType: searchParams.get("grass") ?? lawnPrefill.grassType,
                sunlight: searchParams.get("sunlight") ?? lawnPrefill.sunlight,
                season: searchParams.get("season") ?? getCurrentSeason(),
                mowerType: searchParams.get("mower") ?? "rotary",
              }}
              onResultChange={handleResultChange}
              onMowerChange={setMowerType}
            />
          </div>

          {/* Live result (updates as inputs change) */}
          {result && (
            <div className="space-y-6">
              <MowingSuccessCard result={result} />
              <MowingStripeStudioCta
                grassType={result.grassType}
                season={result.currentSeason}
                mowerType={mowerType}
              />
              <ResultEmailCaptureCard
                source="tool_result_mowing"
                toolDisplayName="mowing plan"
                subcopy={`Your 12-month ${result.grassType} lawn calendar, starting with this month's tasks. Arrives right away.`}
                successSubcopy="Your 12-month calendar is on the way. Your mowing plan stays right here to revisit anytime."
                grassType={result.grassType}
                zipCode={searchParams.get("zip") ?? lawnPrefill.zipCode}
                accountLead={{
                  redirectTo: buildToolUrl("mowing-calculator", {
                    grassType: result.grassType,
                  }),
                  headline: `Save my mowing plan for ${result.grassType}`,
                  subcopy: "Keep this mowing plan in a free account and track your lawn over time.",
                  // Grass-aware (grassType + zipCode are passed), so the subscribe
                  // route sends the 12-month calendar. Name it, per the honesty
                  // rules in .claude/rules/tools-pages.md.
                  dividerLabel: "or get your 12-month calendar by email",
                  onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_mowing"),
                }}
                onCaptured={() => {
                  closeNewsletterModal()
                }}
              />
              <MowingResults result={result} />
              <ProductGuideCta source="mowing_calculator_result" variant="result" />
            </div>
          )}

          <RelatedTools
            currentToolId="mowing-calculator"
            prefillValues={{
              zip: lawnPrefill.zipCode,
              grassType: result?.grassType ?? lawnPrefill.grassType,
              lawnSize: lawnPrefill.lawnSizeSqft,
              sunlight: searchParams.get("sunlight") ?? lawnPrefill.sunlight,
              season: searchParams.get("season") ?? undefined,
            }}
          />

          {!result && (
            <>
              <div className="mt-8">
                <ProductRecommendations
                  categories={["Lawn Mowers & Maintenance Tools"]}
                  title="Mowing & Maintenance Tools"
                  description="Top-rated mowers and lawn maintenance equipment"
                  trackingContext="mowing-calculator-pre-submission"
                  compact
                />
              </div>
            </>
          )}
        </div>
      </Reveal>

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Mowing Height Calculator"
        usageCount={usageCount}
        grassType={result?.grassType}
        zipCode={searchParams.get("zip") ?? lawnPrefill.zipCode}
      />
    </div>
  )
}

/**
 * Bounded peak-moment frame for the mowing result. Hero = recommended mow
 * height for the current season (the single most actionable number). Next
 * action: fertilizer calculator (mowing and feeding cycles go together).
 */
function MowingSuccessCard({ result }: { result: MowingResult }) {
  const heightLabel = result.currentRecommendations.height
  const season = result.currentSeason ?? "this season"
  const freq = result.currentRecommendations.frequency

  // Derive the cm value from the formatted height string ("X inches" / "X.X inches").
  // formatHeight always emits that pattern so the parse is safe; fall back to "—"
  // on any unexpected shape so the toggle degrades gracefully.
  const inchesNum = parseFloat(heightLabel)
  const cmLabel = Number.isFinite(inchesNum)
    ? `${(inchesNum * 2.54).toFixed(1)}`
    : "—"

  const fertilizerHref = buildRelatedToolUrl("fertilizer-calculator", "mowing-calculator", {
    grassType: result.grassType,
  })

  const handleCtaClick = () => {
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: "mowing-calculator",
      targetTool: "fertilizer-calculator",
      rank: 1,
      placement: "success_card",
      recommendationReason: "Mowing and feeding cycles go hand in hand",
      prefillFields: "grass",
    })
  }

  return (
    <ResultSuccessCard
      heroValue={inchesNum.toFixed(Number.isInteger(inchesNum) ? 0 : 1)}
      heroUnit="in"
      heroLabel={`mow height for your ${result.grassType} lawn`}
      reframe={{
        ariaLabel: "Show mow height in inches or centimeters",
        defaultKey: "inches",
        analyticsEvent: "mowing_view_toggled",
        analyticsProps: { grass_type: result.grassType, season: result.currentSeason },
        options: [
          { key: "inches", label: "Inches" },
          { key: "cm", label: "CM" },
        ],
        values: {
          inches: {
            value: inchesNum.toFixed(Number.isInteger(inchesNum) ? 0 : 1),
            unit: "in",
            label: `mow height for your ${result.grassType} lawn`,
          },
          cm: {
            value: cmLabel,
            unit: "cm",
            label: `mow height for your ${result.grassType} lawn`,
          },
        },
      }}
      interpretation={
        <>
          Keep your {result.grassType} at {heightLabel} {season} and mow {freq}. Never remove more than one-third of the blade in a single cut.
        </>
      }
      cta={{
        href: fertilizerHref,
        label: "Calculate your fertilizer needs",
        onClick: handleCtaClick,
      }}
      share={{
        url: buildToolUrl("mowing-calculator", {
          grassType: result.grassType,
          season: result.currentSeason,
        }),
        title: "Mowing Schedule",
        text: `Mowing schedule for my ${result.grassType} lawn`,
        trackingId: "mowing-calculator",
      }}
    />
  )
}

export function MowingCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="mowing-calculator" />}>
      <SearchParamsHydrationGate dateSensitive>
        <MowingCalculatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
